import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Placement } from "@arena/shared";

/** The arrowhead grows out of the local player's ring (outer radius about 0.72 m), "O>". */
const START = 0.66;
const LENGTH = 0.42;
const WIDTH = 0.46;

/**
 * Where the next move goes (D43). The 2D sprites only look left or right on
 * screen: the real direction, all round, is drawn on the floor in front of the
 * local fighter. It follows the stick while it is pushed (a move started now,
 * or chained, turns that way), else the facing. Faint at rest, bright when
 * steering. A sight marks the enemy an aim-assisted shot would lock onto.
 */
export class AimGuide {
  private readonly root: TransformNode;
  private readonly arrow: Mesh;
  private readonly arrowMaterial: StandardMaterial;
  private readonly sight: Mesh;
  private readonly sightMaterial: StandardMaterial;
  private time = 0;

  constructor(scene: Scene) {
    this.root = new TransformNode("aim-guide", scene);
    this.arrow = CreateGround("aim-arrow", { width: WIDTH, height: LENGTH }, scene);
    this.arrow.parent = this.root;
    this.arrow.position.z = START + LENGTH / 2;
    this.arrowMaterial = decal(scene, "aim-arrow", arrowTexture(scene));
    this.arrow.material = this.arrowMaterial;
    this.sight = CreateGround("aim-sight", { width: 1.5, height: 1.5 }, scene);
    this.sightMaterial = decal(scene, "aim-sight", sightTexture(scene));
    this.sight.material = this.sightMaterial;
    for (const mesh of [this.arrow, this.sight]) {
      mesh.isPickable = false;
      mesh.alwaysSelectAsActiveMesh = true;
    }
    this.root.setEnabled(false);
    this.sight.setEnabled(false);
  }

  /**
   * `feet`: the local fighter on the floor, null when it cannot act. `aim`: unit
   * direction on the ground. `lock`: the floor under the enemy a shot would hit.
   */
  update(feet: Placement | null, aim: { x: number; z: number }, steering: boolean, lock: Placement | null, dt: number): void {
    this.time += dt;
    this.root.setEnabled(feet !== null);
    this.sight.setEnabled(feet !== null && lock !== null);
    if (!feet) return;
    this.root.position.set(feet.x, feet.y + 0.03, feet.z);
    this.root.rotation.y = Math.atan2(aim.x, aim.z);
    this.arrowMaterial.alpha = steering ? 0.8 : 0.3;
    if (!lock) return;
    this.sight.position.set(lock.x, lock.y + 0.04, lock.z);
    this.sight.rotation.y = this.time * 1.6;
    this.sightMaterial.alpha = 0.7 + 0.25 * Math.sin(this.time * 9);
  }

  dispose(): void {
    this.arrow.dispose(false, true);
    this.sight.dispose(false, true);
    this.root.dispose();
  }
}

function decal(scene: Scene, name: string, texture: DynamicTexture): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.disableLighting = true;
  material.emissiveColor = Color3.White();
  material.backFaceCulling = false;
  return material;
}

/** An arrowhead, gold like the local ring with a dark edge; its notched base sits on the ring. */
function arrowTexture(scene: Scene): DynamicTexture {
  const w = 32;
  const h = 30;
  const texture = new DynamicTexture("aim-arrow", { width: w, height: h }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const g = texture.getContext() as unknown as CanvasRenderingContext2D;
  g.clearRect(0, 0, w, h);
  g.lineJoin = "round";
  // The far end is the top of the image: the tip.
  g.beginPath();
  g.moveTo(w / 2, 3);
  g.lineTo(w - 4, h - 3);
  g.lineTo(w / 2, h - 11);
  g.lineTo(4, h - 3);
  g.closePath();
  g.strokeStyle = "rgba(40, 26, 8, 0.7)";
  g.lineWidth = 4;
  g.stroke();
  g.fillStyle = "#f2c14e";
  g.fill();
  texture.hasAlpha = true;
  texture.update();
  return texture;
}

/** A ring with four ticks: the enemy the pistol locks onto. */
function sightTexture(scene: Scene): DynamicTexture {
  const size = 64;
  const texture = new DynamicTexture("aim-sight", { width: size, height: size }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const g = texture.getContext() as unknown as CanvasRenderingContext2D;
  g.clearRect(0, 0, size, size);
  g.strokeStyle = "#ff6b5b";
  g.lineWidth = 3;
  g.beginPath();
  g.arc(size / 2, size / 2, 22, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 4;
  for (const [x1, y1, x2, y2] of [[32, 2, 32, 14], [32, 50, 32, 62], [2, 32, 14, 32], [50, 32, 62, 32]] as const) {
    g.beginPath();
    g.moveTo(x1, y1);
    g.lineTo(x2, y2);
    g.stroke();
  }
  texture.hasAlpha = true;
  texture.update();
  return texture;
}
